import { Box, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import MuzukuruLogo from "../../../../../../../assets/muzukuru.png";
import apiService from "../../../../../../../service/Api";
import { COVER_TEMPLATES } from "../../../../../../proposal/proposal-preview/templates/coverTemplates";
import { CoverPageTemplate } from "../../../../types/cover.types";
import { Proposal } from "../../../../types/proposal.types";
import letterheadpattern from "./pages/assets/letterheadpattern.png";
import ProposalLetterTemplate from "./pages/components/ProposalLetterHead";
import CoverPage from "./pages/CoverPage";
import FrequentlyUsedServicesPage from "./pages/FrequentlyUsedServices";
import MainProposalPage from "./pages/MainProposalPage";
import MemorialPage from "./pages/MemorialPage";
import ProposalServicesPage from "./pages/ProposalServicesPage";
import WhatsAppPreviewPage from "./pages/WhatsMessagePreviewPage";
import AgreementFormPage from "./pages/AgreementForm";
import TeamPage from "./pages/BackPage";
import ServicePage from "./pages/AdditionalServicesPage";
import PageSelectionModal from "./PageSelectionModal";

const PAGES = [
  { id: "cover", label: "Cover Page" },
  { id: "main", label: "Letter & Overview" },
  { id: "memorial", label: "Memorial Page" },
  { id: "frequently", label: "Frequently Used Services" },
  { id: "services", label: "Included Services" },
  { id: "service", label: "Services Page" },
  { id: "whatsapp", label: "WhatsApp Preview" },
  { id: "agreement", label: "Agreement Form" },
  { id: "backpage", label: "Back Page" },
];

const PAGE_WIDTH = 816;
const PAGE_HEIGHT = 1056;

const MAIN_SCALE = 360 / PAGE_HEIGHT;
const MAIN_WIDTH = PAGE_WIDTH * MAIN_SCALE;

const THUMB_WIDTH = 160;
const THUMB_HEIGHT = Math.round(THUMB_WIDTH * (4 / 3));
const THUMB_SCALE = THUMB_WIDTH / PAGE_WIDTH;

interface ProposalPagesPreviewProps {
  proposal: Proposal;
  previousStreams?: { id?: string; title: string; url: string }[];
  pageSelectionOpen: boolean;
  isGenerating: boolean;
  onClosePageSelection: () => void;
  onConfirmDownload: (pages: string[]) => void;
  selectedCoverTemplate?: CoverPageTemplate | null;
}

export default function ProposalPagesPreview({
  proposal,
  previousStreams,
  pageSelectionOpen,
  isGenerating,
  onClosePageSelection,
  onConfirmDownload,
  selectedCoverTemplate,
}: ProposalPagesPreviewProps) {
  const [activePage, setActivePage] = useState(0);
  const [streamId, setStreamId] = useState<number | null>(null);
  const [template, setTemplate] = useState<CoverPageTemplate | null>(null);

  useEffect(() => {
    if (selectedCoverTemplate) {
      setTemplate(selectedCoverTemplate);
      return;
    }
    const bookingType = proposal.booking?.booking_type ?? "FUNERAL";
    const category = bookingType === "CONFERENCE" ? "conference" : "funeral";
    const matched = COVER_TEMPLATES.filter(
      (t) => t.is_active && t.category.toLowerCase() === category,
    );
    if (matched.length > 0) setTemplate(matched[0]);
  }, [selectedCoverTemplate, proposal.booking?.booking_type]);

  useEffect(() => {
    if (proposal.booking_id) {
      apiService
        .get<{ results: { id: number }[] }>(
          `api/streams/stream/?booking=${proposal.booking_id}`,
        )
        .then((res) => {
          console.log("streams response", res);
          const results = res.data?.results;
          if (results?.length > 0) {
            setStreamId(results[0].id);
          }
        })
        .catch(() => {});
    }
  }, [proposal.booking_id]);

  const renderPage = (pageId: string) => {
    switch (pageId) {
      case "cover":
        return <CoverPage proposal={proposal} template={template} />;
      case "main":
        return (
          <>
            <ProposalLetterTemplate
              companyInfo={{
                name: "Muzukuru Media",
                address: "46 Mt Pleasant Drive, Harare",
                phone2: "+263 776 402 402",
                phone1: "+263 774 808 392 (Bex)",
                email: "sales@muzukuru.com",
                logo: MuzukuruLogo,
              }}
              backgroundPattern={letterheadpattern}
            />
            <MainProposalPage
              proposal={proposal}
              streamId={streamId ?? 0}
              previousStreams={previousStreams}
            />
          </>
        );
      case "services":
        return <ProposalServicesPage proposal={proposal} />;
      case "memorial":
        return <MemorialPage />;
      case "frequently":
        return <FrequentlyUsedServicesPage />;
      case "service":
        return <ServicePage />;
      case "whatsapp":
        return (
          <WhatsAppPreviewPage proposal={proposal} streamId={streamId ?? 0} />
        );
      case "agreement":
        return <AgreementFormPage proposal={proposal} />;
      case "backpage":
        return <TeamPage />;
      default:
        return null;
    }
  };

  return (
    <>
      <PageSelectionModal
        open={pageSelectionOpen}
        onClose={onClosePageSelection}
        onConfirm={onConfirmDownload}
        availablePages={PAGES}
        isGenerating={isGenerating}
        renderThumbnail={(pageId) => renderPage(pageId)}
        actionType="download"
      />
      <Paper
        sx={{
          borderRadius: 3,
          p: 2,
          boxShadow: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 500,
          mb: 2,
        }}
      >
        {/* Main preview */}
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderRadius: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: 360,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: MAIN_WIDTH,
              height: 360,
              borderRadius: 2,
              border: "2px solid #c59a70",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: PAGE_WIDTH,
                height: PAGE_HEIGHT,
                transform: `scale(${MAIN_SCALE})`,
                transformOrigin: "top left",
                pointerEvents: "auto",
              }}
            >
              {renderPage(PAGES[activePage].id)}
            </Box>
          </Box>
        </Box>

        {/* Thumbnails */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            px: 1,
            justifyContent: PAGES.length <= 4 ? "center" : "flex-start",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#e2e8f0",
              borderRadius: 4,
            },
          }}
        >
          {PAGES.map((page, index) => (
            <Box
              key={page.id}
              onClick={() => setActivePage(index)}
              sx={{
                flexShrink: 0,
                width: THUMB_WIDTH,
                height: THUMB_HEIGHT,
                borderRadius: "8px",
                border:
                  index === activePage
                    ? "2px solid #c59a70"
                    : "2px solid #d1d5db",
                overflow: "hidden",
                cursor: "pointer",
                transition: "border-color 0.15s",
                bgcolor: index === activePage ? "#f0e6da" : "#ffffff",
                "&:hover": {
                  borderColor: index === activePage ? "#c59a70" : "#d1d5db",
                },
              }}
            >
              <Box
                sx={{
                  width: PAGE_WIDTH,
                  height: PAGE_HEIGHT,
                  transform: `scale(${THUMB_SCALE})`,
                  transformOrigin: "top left",
                  pointerEvents: "none",
                }}
              >
                {renderPage(page.id)}
              </Box>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            position: "fixed",
            top: "-10000px",
            left: "-10000px",
            width: "816px",
          }}
        >
          <Box id="cover-page-pdf">
            <CoverPage proposal={proposal} template={template} />
          </Box>
          <Box id="main-page-pdf">
            <ProposalLetterTemplate
              companyInfo={{
                name: "Muzukuru Media",
                address: "46 Mt Pleasant Drive, Harare",
                phone2: "+263 776 402 402",
                phone1: "+263 774 808 392 (Bex)",
                email: "sales@muzukuru.com",
                logo: MuzukuruLogo,
              }}
              backgroundPattern={letterheadpattern}
            />
            <MainProposalPage
              proposal={proposal}
              streamId={streamId ?? 0}
              previousStreams={previousStreams}
            />
          </Box>
          <Box id="memorial-page-pdf">
            <MemorialPage />
          </Box>
          <Box id="frequently-page-pdf">
            <FrequentlyUsedServicesPage />
          </Box>
          <Box id="services-page-pdf">
            <ProposalServicesPage proposal={proposal} />
          </Box>
          <Box id="service-page-pdf">
            <ServicePage />
          </Box>
          <Box id="whatsapp-page-pdf">
            <WhatsAppPreviewPage proposal={proposal} streamId={streamId ?? 0} />
          </Box>
          <Box id="agreement-page-pdf">
            <AgreementFormPage proposal={proposal} />
          </Box>
          <Box id="backpage-page-pdf">
            <TeamPage />
          </Box>
        </Box>
      </Paper>
    </>
  );
}
